<script setup lang="ts">
import { onMounted, ref } from 'vue';
import {
  DxDataGrid,
  DxColumn,
  DxSorting,
  DxScrolling,
  DxMasterDetail,
  DxButton as DxGridButton,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import {
  BottoPagePanel,
  ConfirmDialog,
  EntityEditPopup,
  bottoGridPaging,
  showError,
  showSuccess,
  type EntityConfig,
} from '@botto/core';
import { aggregatorsStore } from '../../api';
import type { Aggregator } from '../../api';
import { getSmppHostsLookup } from '../../lookups';
import AggregatorDirectionsPanel from './AggregatorDirectionsPanel.vue';

const { paging, pager } = bottoGridPaging();
const grid = ref<InstanceType<typeof DxDataGrid> | null>(null);
const smppHostsLookup = getSmppHostsLookup();

const yesNoItems = ['yes', 'no'];
const systemTypeItems = ['VMS', 'VMA', 'OTA', 'NULL'];
const smppTypeItems = ['provider', 'client'];
const transportTypeItems = ['TCP', 'TLS'];
const linkReplacementItems = [
  {
    value: true,
    text: 'С подменой ссылок (ошибка, если нет домена)',
  },
  {
    value: false,
    text: 'Без обязательной подмены (если домен есть — меняем)',
  },
];

const popupOpen = ref(false);
const popupMode = ref<'create' | 'edit'>('create');
const popupModel = ref<Record<string, unknown>>({});
const confirmOpen = ref(false);
const confirmTarget = ref<Aggregator | null>(null);

function connectionStatusCell(cellElement: HTMLElement, cellInfo: { data?: Aggregator }) {
  const row = cellInfo.data ?? {};
  const wrap = document.createElement('span');
  wrap.style.display = 'inline-flex';
  wrap.style.alignItems = 'center';
  wrap.style.gap = '6px';

  const text = document.createElement('span');
  text.textContent = row.connection_status ?? '';
  wrap.appendChild(text);

  if (row.status_description) {
    const icon = document.createElement('i');
    icon.className = 'dx-icon dx-icon-help';
    icon.title = row.status_description;
    icon.style.cursor = 'help';
    icon.style.opacity = '0.65';
    icon.style.fontSize = '16px';
    wrap.appendChild(icon);
  }

  cellElement.appendChild(wrap);
}

const config: EntityConfig<Aggregator> = {
  title: 'Агрегатор',
  titlePlural: 'Агрегаторы',
  popupWidth: 960,
  popupHeight: 'auto',
  formColCount: 3,
  tabs: ['SMPP', 'Метрики'],
  columns: [],
  fields: [
    { dataField: 'id', label: 'ID', editOnly: true, editorOptions: { readOnly: true } },
    {
      dataField: 'smsc_id',
      label: 'SMSC ID',
      tab: 'SMPP',
      digitsOnly: true,
      helpText: 'Пусто при создании — сгенерирует бэкенд',
    },
    { dataField: 'description', label: 'Описание', required: true, tab: 'SMPP', colSpan: 3 },
    { dataField: 'host', label: 'Host', required: true, tab: 'SMPP' },
    { dataField: 'port', label: 'Port', required: true, tab: 'SMPP' },
    { dataField: 'smsc_username', label: 'SMSC username', required: true, tab: 'SMPP' },
    {
      dataField: 'smsc_password',
      label: 'SMSC password',
      tab: 'SMPP',
      editorOptions: { mode: 'password' },
    },
    {
      dataField: 'enabled',
      label: 'Включён',
      tab: 'SMPP',
      editorType: 'dxSelectBox',
      editorOptions: { items: yesNoItems },
    },
    {
      dataField: 'aggregator_type',
      label: 'Тип агрегатора',
      tab: 'SMPP',
      helpText: 'Пусто — задаст бэкенд',
    },
    {
      dataField: 'smpp_type',
      label: 'SMPP type',
      tab: 'SMPP',
      editorType: 'dxSelectBox',
      editorOptions: { items: smppTypeItems },
    },
    {
      dataField: 'transport_type',
      label: 'Transport type',
      tab: 'SMPP',
      editorType: 'dxSelectBox',
      editorOptions: { items: transportTypeItems },
    },
    {
      dataField: 'account_id',
      label: 'Account ID',
      tab: 'SMPP',
      editorType: 'dxNumberBox',
      helpText: 'Пусто — задаст бэкенд',
    },
    {
      dataField: 'link_replacement',
      label: 'Подмена ссылок',
      tab: 'SMPP',
      editorType: 'dxSelectBox',
      editorOptions: {
        items: linkReplacementItems,
        valueExpr: 'value',
        displayExpr: 'text',
      },
      helpText:
        'true — с подменой + ошибка без домена; false — без обязательной подмены, но домен при наличии всё равно применяется',
    },
    {
      dataField: 'system_type',
      label: 'System type',
      tab: 'SMPP',
      editorType: 'dxSelectBox',
      editorOptions: { items: systemTypeItems },
    },
    {
      dataField: 'smpp_host',
      label: 'SMPP host',
      tab: 'SMPP',
      lookup: smppHostsLookup,
      helpText: 'Каталог GET /smpp_hosts/ (значение = smpp_host)',
    },
    { dataField: 'enquire_link_interval', label: 'Enquire link interval', tab: 'SMPP' },
    {
      dataField: 'avg_delivery_time',
      label: 'Avg delivery time',
      tab: 'Метрики',
      editorType: 'dxNumberBox',
    },
    {
      dataField: 'msg_rate_limit_per',
      label: 'Msg rate limit',
      tab: 'Метрики',
      editorType: 'dxNumberBox',
    },
    { dataField: 'created', label: 'Создан', editOnly: true, hidden: true },
    { dataField: 'updated', label: 'Обновлён', editOnly: true, hidden: true },
  ],
};

function refresh() {
  grid.value?.instance?.refresh();
}

function openCreate() {
  popupMode.value = 'create';
  popupModel.value = { link_replacement: true };
  popupOpen.value = true;
}

function openEdit(row: Aggregator) {
  popupMode.value = 'edit';
  popupModel.value = JSON.parse(JSON.stringify(row));
  popupOpen.value = true;
}

async function handleSave(payload: Record<string, unknown>) {
  try {
    if (popupMode.value === 'create') {
      await aggregatorsStore.insert(payload as Aggregator);
      showSuccess('Создано');
    } else {
      const id = payload.id as number;
      const { id: _omit, ...rest } = payload;
      await aggregatorsStore.update(id, rest as Aggregator);
      showSuccess('Сохранено');
    }
    popupOpen.value = false;
    refresh();
  } catch (e) {
    showError(e);
    throw e;
  }
}

function confirmDelete(row: Aggregator) {
  confirmTarget.value = row;
  confirmOpen.value = true;
}

async function doDelete() {
  if (!confirmTarget.value?.id) return;
  try {
    await aggregatorsStore.remove(confirmTarget.value.id);
    showSuccess('Удалено');
    confirmOpen.value = false;
    confirmTarget.value = null;
    refresh();
  } catch (e) {
    showError(e);
  }
}

onMounted(() => {
  void smppHostsLookup.ensureLoaded();
  refresh();
});
</script>

<template>
  <BottoPagePanel title="Агрегаторы" standalone>
    <template #actions>
      <DxButton icon="add" text="Добавить" type="default" styling-mode="contained" @click="openCreate" />
      <DxButton icon="refresh" text="Обновить" styling-mode="outlined" @click="refresh" />
    </template>

    <DxDataGrid
      ref="grid"
      :data-source="aggregatorsStore"
      key-expr="id"
      :remote-operations="{ paging: true, sorting: true, filtering: false }"
      :show-borders="false"
      :show-row-lines="false"
      :show-column-lines="false"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :column-auto-width="true"
      :allow-column-resizing="true"
      :paging="paging"
      :pager="pager"
      height="100%"
      width="100%"
      @row-dbl-click="(e: any) => openEdit(e.data)"
    >
      <DxColumn data-field="id" caption="ID" data-type="number" :width="80" />
      <DxColumn data-field="smsc_id" caption="SMSC ID" :width="120" />
      <DxColumn data-field="description" caption="Описание" />
      <DxColumn data-field="host" caption="Host" :width="140" />
      <DxColumn data-field="enabled" caption="Включён" :width="90" />
      <DxColumn
        data-field="connection_status"
        caption="Статус подключения"
        :width="180"
        :cell-template="connectionStatusCell"
      />
      <DxColumn data-field="aggregator_type" caption="Тип" :width="100" />
      <DxColumn
        data-field="link_replacement"
        caption="Подмена ссылок"
        :width="130"
        :calculate-cell-value="(row: Aggregator) => (row.link_replacement === false ? 'Нет' : 'Да')"
      />

      <DxColumn type="buttons" :width="100" caption="Действия">
        <DxGridButton hint="Редактировать" icon="edit" :on-click="(e: any) => openEdit(e.row.data)" />
        <DxGridButton hint="Удалить" icon="trash" :on-click="(e: any) => confirmDelete(e.row.data)" />
      </DxColumn>

      <DxMasterDetail :enabled="true" template="detail" />
      <template #detail="{ data: row }">
        <AggregatorDirectionsPanel v-if="row.data.id != null" :aggregator-id="row.data.id" />
      </template>

      <DxSorting mode="single" />
      <DxScrolling mode="standard" />
    </DxDataGrid>
  </BottoPagePanel>

  <EntityEditPopup
    v-if="popupOpen"
    v-model:visible="popupOpen"
    :mode="popupMode"
    :config="config"
    :initial-value="popupModel"
    @save="handleSave"
  />

  <ConfirmDialog
    v-if="confirmOpen"
    v-model:visible="confirmOpen"
    title="Удалить запись?"
    message="Действие необратимо."
    ok-text="Удалить"
    ok-type="danger"
    @ok="doDelete"
  />
</template>

<style scoped>
:deep(.dx-datagrid-rowsview .dx-master-detail-row:not(.dx-datagrid-edit-form) > .dx-datagrid-group-space),
:deep(.dx-datagrid-rowsview .dx-master-detail-row:not(.dx-datagrid-edit-form) .dx-master-detail-cell) {
  background-color: transparent;
}
</style>
